import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Menu, X, LayoutDashboard, User, LogOut, Phone } from "lucide-react";

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center rounded-full border border-white/20 overflow-hidden text-xs ${className}`} data-testid="lang-toggle">
      <button data-testid="lang-en" onClick={() => setLang("en")}
        className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-white text-black" : "text-white/70 hover:text-white"}`}>EN</button>
      <button data-testid="lang-fr" onClick={() => setLang("fr")}
        className={`px-3 py-1.5 transition-colors ${lang === "fr" ? "bg-white text-black" : "text-white/70 hover:text-white"}`}>FR</button>
    </div>
  );
};

export const Navbar = ({ dark = true }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const phone = t("hero.phone");
  const tel = `tel:+1${phone.replace(/[^0-9]/g, "")}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  const navTo = (hash) => {
    if (isHome) {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + hash);
    }
    setOpen(false);
  };

  return (
    <>
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "backdrop-blur-2xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none group flex-shrink-0">
          <span className="font-serif text-2xl tracking-tight text-white whitespace-nowrap">Autobus Limo</span>
          <span className="text-[10px] tracking-luxe uppercase text-muted-foreground mt-0.5">
            Mont Royal
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5 text-sm text-white/80 [&_button]:whitespace-nowrap">
          <button data-testid="nav-services" onClick={() => navTo("#services")} className="hover:text-white transition-colors">
            {t("nav.services")}
          </button>
          <button data-testid="nav-fleet" onClick={() => navTo("#fleet")} className="hover:text-white transition-colors">
            {t("nav.fleet")}
          </button>
          <button data-testid="nav-airport" onClick={() => { navigate("/airport-transfers"); setOpen(false); }} className="hover:text-white transition-colors">
            {t("nav.airportShort")}
          </button>
          <button data-testid="nav-corporate" onClick={() => { navigate("/corporate"); setOpen(false); }} className="hover:text-white transition-colors">
            {t("nav.corporate")}
          </button>
          <button data-testid="nav-weddings" onClick={() => { navigate("/weddings-events"); setOpen(false); }} className="hover:text-white transition-colors">
            {t("nav.weddingsShort")}
          </button>
          <button data-testid="nav-groups" onClick={() => { navigate("/group-transportation"); setOpen(false); }} className="hover:text-white transition-colors">
            {t("nav.groupsShort")}
          </button>
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <LangToggle />
          <a href={tel} data-testid="nav-call-link" className="hidden 2xl:flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors px-2 whitespace-nowrap">
            <Phone className="h-4 w-4" /> {phone}
          </a>
          {user && user.role === "admin" && (
            <Button data-testid="nav-admin-btn" variant="ghost" className="text-white hover:bg-white/10"
              onClick={() => navigate("/admin")}>
              <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav.dashboard")}
            </Button>
          )}
          {user ? (
            <>
              <Button data-testid="nav-account-btn" variant="ghost" className="text-white hover:bg-white/10"
                onClick={() => navigate("/account")}>
                <User className="h-4 w-4 mr-2" /> {user.name?.split(" ")[0] || t("nav.account")}
              </Button>
              <Button data-testid="nav-logout-btn" variant="ghost" size="icon" className="text-white hover:bg-white/10"
                onClick={() => { logout(); navigate("/"); }}>
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button data-testid="nav-login-btn" variant="ghost" className="text-white hover:bg-white/10"
              onClick={() => navigate("/login")}>
              {t("nav.signin")}
            </Button>
          )}
          <Button data-testid="nav-book-btn"
            className="rounded-full bg-white text-black hover:bg-white/85 px-6"
            onClick={() => navigate("/book")}>
            {t("nav.getQuote")}
          </Button>
        </div>

        <div className="xl:hidden flex items-center gap-3">
          <LangToggle />
          <button data-testid="nav-mobile-toggle" className="text-white" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden backdrop-blur-2xl bg-black/90 border-t border-white/10 px-6 py-6 flex flex-col gap-4">
          <button onClick={() => navTo("#services")} className="text-white/80 text-left py-1">{t("nav.services")}</button>
          <button onClick={() => navTo("#fleet")} className="text-white/80 text-left py-1">{t("nav.fleet")}</button>
          <button onClick={() => { navigate("/airport-transfers"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.airport")}</button>
          <button onClick={() => { navigate("/corporate"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.corporate")}</button>
          <button onClick={() => { navigate("/weddings-events"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.weddings")}</button>
          <button onClick={() => { navigate("/group-transportation"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.groups")}</button>
          <button onClick={() => { navigate("/service-areas"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.areas")}</button>
          <button onClick={() => navTo("#process")} className="text-white/80 text-left py-1">{t("nav.process")}</button>
          <button onClick={() => navTo("#faq")} className="text-white/80 text-left py-1">FAQ</button>
          <a href={tel} className="flex items-center gap-2 text-white/80 py-1"><Phone className="h-4 w-4" /> {phone}</a>
          {user && user.role === "admin" && (
            <button onClick={() => { navigate("/admin"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.dashboard")}</button>
          )}
          {user ? (
            <>
              <button onClick={() => { navigate("/account"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.myAccount")}</button>
              <button onClick={() => { logout(); navigate("/"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.signout")}</button>
            </>
          ) : (
            <button onClick={() => { navigate("/login"); setOpen(false); }} className="text-white/80 text-left py-1">{t("nav.signin")}</button>
          )}
          <Button className="rounded-full bg-white text-black hover:bg-white/85 mt-2"
            onClick={() => { navigate("/book"); setOpen(false); }}>{t("nav.getQuote")}</Button>
        </div>
      )}
    </header>

    {/* Mobile sticky bottom bar */}
    <div data-testid="mobile-bottom-bar"
      className="xl:hidden fixed bottom-0 left-0 right-0 z-50 grid grid-cols-2 border-t border-white/10 bg-black/90 backdrop-blur-xl">
      <a href={tel} data-testid="mobile-call-btn"
        className="flex items-center justify-center gap-2 py-3.5 text-sm text-white border-r border-white/10">
        <Phone className="h-4 w-4" /> {t("nav.call")}
      </a>
      <button data-testid="mobile-quote-btn" onClick={() => navigate("/book")}
        className="flex items-center justify-center gap-2 py-3.5 text-sm font-medium bg-white text-black">
        {t("nav.getQuote")}
      </button>
    </div>
    </>
  );
};
